import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { FilterMatchMode } from 'primereact/api';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { Sidebar } from 'primereact/sidebar';
import Loader from "../Shared/Loader/Loader";
import APIConstant from "../../utils/pathConstants";
import api from "../../utils/apiClient";
import { apiBase } from '../../utils/config';
import CommonConfig from '../../utils/constant';
import Toast from '../Shared/Toast/Toast';
import DeleteConfirmation from '../Dialogs/DeleteConfirmation';
import { useCatchResponse } from '../../utils/useCatchResponse';
import AddEditAnnouncement from './AddEditAnnouncement';
import moment from 'moment';

const Announcement = () => {
    const { catchResponse } = useCatchResponse();
    const [list, setList] = useState([]);
    const [masterData, setMasterData] = useState(null);
    const [visible, setVisible] = useState(false);
    const [filters, setFilters] = useState({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [securityUserID, setSecurityUserID] = useState('');
    const [schoolId, setSchoolId] = useState('');
    const [financialYear, setFinancialYear] = useState('');
    const [rows, setRows] = useState(10);
    const [first, setFirst] = useState(0);
    const [isDeleteConfirmation, setIsDeleteConfirmation] = useState(false);
    const [deleteId, setDeleteId] = useState('');
    const [showReadDetails, setShowReadDetails] = useState(false);
    const [readDetails, setReadDetails] = useState([]);
    const [readTotal, setReadTotal] = useState(0);
    const [pageAccess, setPageAccess] = useState({});

    useEffect(() => {
        const init = async () => {
            const loginData = CommonConfig.loginData();
            const userId = loginData?._id;
            const schoolid = CommonConfig.isOrgOwner(loginData?.UserRole) ? userId : loginData?.CreatedBy;
            setSecurityUserID(userId);
            setSchoolId(schoolid);

            const accessData = CommonConfig.getUserAccess('Announcement');
            setPageAccess(accessData);

            const years = await CommonConfig.getFinancialYears(schoolid);
            const currentYear = CommonConfig.getCurrentFinancialYear(years);
            const fy = CommonConfig.getCookie('selectedAcademicYear') ?? currentYear?.value;
            setFinancialYear(fy);
            getList(schoolid, fy);
        };
        init();
    }, []);

    const getList = async (sid, fy) => {
        try {
            Loader.show();
            const response = await api.post(APIConstant.path.GetAnnouncementList, { SchoolId: sid || schoolId, FinancialYearId: fy || financialYear });
            if (response.success) setList(response.data || []);
            Loader.hide();
        } catch (err) { Loader.hide(); catchResponse(err); }
    };

    const onGlobalFilterChange = (e) => {
        let _filters = { ...filters };
        _filters['global'].value = e.target.value;
        setFilters(_filters);
        setGlobalFilterValue(e.target.value);
    };

    const onCancel = () => { setVisible(false); setMasterData(null); getList(schoolId, financialYear); };

    const handleDelete = async () => {
        try {
            const response = await api.post(APIConstant.path.DeleteAnnouncement, { id: deleteId });
            if (response.success) { Toast.success({ message: 'Deleted successfully' }); getList(schoolId, financialYear); }
            setIsDeleteConfirmation(false); setDeleteId('');
        } catch (err) { setIsDeleteConfirmation(false); catchResponse(err); }
    };

    const handleTogglePin = async (id) => {
        try {
            const response = await api.post(APIConstant.path.ToggleAnnouncementPin, { id });
            if (response.success) { Toast.success({ message: response.message }); getList(schoolId, financialYear); }
        } catch (err) { catchResponse(err); }
    };

    const handleViewRead = async (id) => {
        try {
            Loader.show();
            const response = await api.post(APIConstant.path.GetAnnouncementReadDetails, { id });
            if (response.success) { setReadDetails(response.data || []); setReadTotal(response.totalRead || 0); setShowReadDetails(true); }
            Loader.hide();
        } catch (err) { Loader.hide(); catchResponse(err); }
    };

    const srNoTemplate = (_, { rowIndex }) => rowIndex + 1;

    const categoryColors = { General: '#0B1B6D', Academic: '#2ECC71', Sports: '#F5A623', Events: '#B1007E', Holidays: '#ef2121', Exam: '#5BC0EB', 'Fee Related': '#1C515E' };
    const priorityColors = { Normal: 'info', Important: 'warning', Urgent: 'danger' };

    const categoryTemplate = (row) => <Tag value={row.Category} style={{ background: categoryColors[row.Category] || '#666' }} />;
    const priorityTemplate = (row) => <Tag value={row.Priority} severity={priorityColors[row.Priority] || 'info'} />;
    const dateTemplate = (row) => moment(row.PublishDate).format('DD/MM/YYYY HH:mm');
    const expiryTemplate = (row) => row.ExpiryDate ? moment(row.ExpiryDate).format('DD/MM/YYYY') : '-';

    const targetTemplate = (row) => {
        if (row.TargetType === 'All') return <Tag value="All" severity="success" />;
        if (row.TargetType === 'Role') return <span>{row.TargetRoles?.join(', ')}</span>;
        return <span>{row.TargetClasses?.length || 0} Classes</span>;
    };

    const statusTemplate = (row) => (
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            {row.IsPinned && <i className="pi pi-bookmark-fill" style={{ color: '#F5A623', fontSize: '12px' }}></i>}
            {row.IsPublished ? <Tag value="Published" severity="success" /> : <Tag value="Scheduled" severity="warning" />}
        </div>
    );

    const readTemplate = (row) => (
        <span style={{ cursor: 'pointer', color: '#2f6fed', fontWeight: 600 }} onClick={() => handleViewRead(row._id)}>
            {row.ReadCount || 0} read
        </span>
    );

    const actionTemplate = (row) => (
        <div style={{ display: 'flex', gap: '4px' }}>
            {pageAccess?.EditAccess && (
                <>
                    <Button icon={row.IsPinned ? "pi pi-bookmark-fill" : "pi pi-bookmark"} className="e-sction-btn p-m-0"
                        tooltip={row.IsPinned ? "Unpin" : "Pin"} tooltipOptions={{ position: 'top' }}
                        onClick={() => handleTogglePin(row._id)} />
                    <Button icon="pi pi-pencil" className="e-sction-btn p-m-0"
                        onClick={() => { setMasterData(row); setVisible(true); }} />
                </>
            )}
            {pageAccess?.DeleteAccess && (
                <Button icon="pi pi-trash" className="e-sction-btn p-m-0"
                    onClick={() => { setDeleteId(row._id); setIsDeleteConfirmation(true); }} />
            )}
        </div>
    );

    return (
        <div className='wayment-owner-dashboard et-master-page'>
            <div className="heading-area p-d-flex p-ai-center p-jc-between">
                <div className="p-d-flex p-ai-baseline">
                    <i className="pi pi-megaphone"></i>
                    <h2>Announcements</h2>
                </div>
                <div className="p-d-flex p-ai-center">
                    <div className="searchbar-area e-searchbar-area">
                        <i className="pi pi-search" />
                        <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Search" />
                    </div>
                    {pageAccess?.AddAccess && <Button icon="pi pi-plus" className="e-h-btn" onClick={() => setVisible(true)} />}
                </div>
            </div>

            <DataTable value={list} paginator rows={rows} first={first} filters={filters} filterDisplay="menu"
                responsiveLayout="scroll" paginatorTemplate="RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink JumpToPageInput"
                rowsPerPageOptions={[10, 20, 50]} currentPageReportTemplate="Showing {first}-{last} of {totalRecords} records"
                onPage={(e) => { setFirst(e.first); setRows(e.rows); }} className='custom-table' scrollHeight='calc(100vh - 120px)' size='small'>
                <Column body={srNoTemplate} header="Sr." style={{ width: '5%' }} />
                <Column field="Title" header="Title" style={{ width: '20%' }} />
                <Column body={categoryTemplate} header="Category" style={{ width: '10%' }} />
                <Column body={priorityTemplate} header="Priority" style={{ width: '8%' }} />
                <Column body={targetTemplate} header="Target" style={{ width: '10%' }} />
                <Column body={dateTemplate} header="Publish Date" style={{ width: '12%' }} />
                <Column body={expiryTemplate} header="Expiry" style={{ width: '10%' }} />
                <Column body={statusTemplate} header="Status" style={{ width: '10%' }} />
                <Column body={readTemplate} header="Read" style={{ width: '7%' }} />
                <Column body={actionTemplate} header="Action" style={{ width: '10%' }} />
            </DataTable>

            <Sidebar header={masterData?._id ? 'Edit Announcement' : 'Add Announcement'} className='dialog-popup' visible={visible}
                onHide={onCancel} draggable={false} position="right">
                <AddEditAnnouncement onCancel={onCancel} masterData={masterData} schoolId={schoolId} financialYear={financialYear} userId={securityUserID} />
            </Sidebar>

            <Dialog visible={isDeleteConfirmation} onHide={() => setIsDeleteConfirmation(false)}
                header={<span>Delete Confirmation</span>}
                footer={<div className="dialog-footer p-d-flex p-ai-center p-jc-end">
                    <Button label="Cancel" className="btn-text-action-secondary" onClick={() => setIsDeleteConfirmation(false)} />
                    <Button label="Delete" className="btn-text-action-primary" onClick={handleDelete} />
                </div>} draggable={false} dismissableMask position="center" style={{ width: '450px' }}>
                <DeleteConfirmation />
            </Dialog>

            <Dialog visible={showReadDetails} onHide={() => setShowReadDetails(false)} header={`Read Details (${readTotal})`}
                style={{ width: '500px' }} draggable={false} dismissableMask>
                <DataTable value={readDetails} size="small" emptyMessage="No one has read yet">
                    <Column body={(_, { rowIndex }) => rowIndex + 1} header="Sr." style={{ width: '10%' }} />
                    <Column field="UserName" header="Name" style={{ width: '40%' }} />
                    <Column field="Phone" header="Phone" style={{ width: '25%' }} />
                    <Column header="Read At" body={(row) => moment(row.ReadAt).format('DD/MM/YYYY HH:mm')} style={{ width: '25%' }} />
                </DataTable>
            </Dialog>
        </div>
    );
};

export default Announcement;
